import { useState, useEffect } from "react";
import { BidFormValues } from "@/components/admin/bids/types";
import { axiosInstance } from "@/pages/auth/auth.context";
import { toast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BidFormSchema } from "@/components/admin/bids/schemas";

/**
 * Outcome of a save. `saved` says the backend accepted it; `eventId` is only
 * present when the response gave us one to keep working with.
 */
interface SaveResult {
  saved: boolean;
  eventId?: string;
}

export const useBidForm = (eventId?: string, bidData?: BidFormValues) => {
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const form = useForm<BidFormValues>({
    resolver: zodResolver(BidFormSchema),
    defaultValues: {
      contactName: "",
      companyName: "",
      email: "",
      phoneNumber: "",
      address: "",
      startTime: new Date(),
      interestedGames: "",
      deliveryInfo: "",
      wantUsToDoRaffle: "NO",
      foodInfo: "",
      dealerSpecialInstructions: "",
      additionalInfo: "",
      splitPayment: true,
      positions: {},
      setupTime: "",
      parkingInfo: "",
    },
  });

  useEffect(() => {
    if (bidData) {
      form.reset(bidData);
    }
  }, [bidData, form]);

  const compileEventPayload = (values: BidFormValues) => {
    const transformedPositions = Object.fromEntries(
      Object.entries(values.positions).map(([key, value]) => [
        key,
        value.quantity,
      ]),
    );

    const payload = {
      contactName: values.contactName,
      companyName: values.companyName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      address: values.address,
      startTime: values.startTime.toISOString(),
      eventLength: values.eventLength,
      attendees: values.attendees,
      deliveryInfo: values.deliveryInfo,
      interestedGames: values.interestedGames,
      hasRaffle: values.hasRaffle,
      wantUsToDoRaffle: values.wantUsToDoRaffle,
      foodInfo: values.foodInfo,
      firmnessLevel: values.firmnessLevel,
      additionalInfo: values.additionalInfo,
      dealerSpecialInstructions: values.dealerSpecialInstructions,
      extraPayPerDealer: values.extraPayPerDealer,
      additionalFee: values.additionalFee,
      discountAmount: values.discountAmount,
      splitPayment: values.splitPayment,
      nameOfLocation: values.nameOfLocation,
      city: values.city,
      positionQuantities: transformedPositions,
      referralCode: values.referralCode?.trim(),
      setupTime: values.setupTime?.trim(),
      parkingInfo: values.parkingInfo?.trim(),
    };

    return payload;
  };
  /**
   * Persists the form. Creating and updating differ only in verb and route, and
   * every caller needs the same success and failure handling, so this is the one
   * place that talks to the save endpoints.
   */
  const saveBid = async (values: BidFormValues): Promise<SaveResult> => {
    try {
      setLoading(true);
      const eventPayload = compileEventPayload(values);

      const isNewEvent = eventId === "new";
      const route = isNewEvent
        ? `${process.env.REACT_APP_BASE_API_URL}event/new`
        : `${process.env.REACT_APP_BASE_API_URL}event/${eventId}`;

      // The update endpoint answers 204, the create endpoint 200.
      const response = isNewEvent
        ? await axiosInstance.post(route, eventPayload)
        : await axiosInstance.put(route, eventPayload);

      if (response.status === 200 || response.status === 204) {
        toast({
          title: "Success",
          description: "Bid details saved successfully",
          variant: "success",
        });
        return {
          saved: true,
          eventId: isNewEvent ? response.data?.id : eventId,
        };
      }

      return { saved: false };
    } catch (error) {
      console.error("Error saving bid details:", error);
      toast({
        title: "Error",
        description: "Failed to save bid details",
        variant: "destructive",
      });
      return { saved: false };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Saves the form, then asks the backend to send something for the saved bid.
   * Send Bid and Send Invoice are the same flow with a different endpoint.
   */
  const saveThenSend = async (
    action: "sendBid" | "sendInvoice",
    successDescription: string,
    errorDescription: string,
  ) => {
    try {
      const { eventId: savedEventId } = await saveBid(form.getValues());
      if (!savedEventId) {
        throw new Error("Event ID not found after save.");
      }

      await axiosInstance.post(`event/${savedEventId}/${action}`);

      toast({
        title: "Success",
        description: successDescription,
        variant: "success",
      });
      navigate("/admin/bids");
    } catch (error) {
      console.error(`Error running ${action}:`, error);
      toast({
        title: "Error",
        description: errorDescription,
        variant: "destructive",
      });
    }
  };

  /** Saves and hands back the bid id, for callers that keep working with it. */
  const handleSave = async (
    values: BidFormValues,
  ): Promise<string | undefined> => (await saveBid(values)).eventId;

  /** Saves and returns to the bids list. */
  const handlePartialSave = async () => {
    const { saved } = await saveBid(form.getValues());
    if (saved) {
      navigate("/admin/bids");
    }
  };

  const handleSendInvoice = () =>
    saveThenSend(
      "sendInvoice",
      "Invoice sent successfully",
      "Failed to send invoice",
    );

  const handleSendBid = () =>
    saveThenSend("sendBid", "Bid sent successfully", "Failed to send bid");

  const handleCreateEvent = async () => {
    const values = form.getValues();
    try {
      const eventPayload = compileEventPayload(values);
      if (eventId === "new") {
        await axiosInstance.post(`event/createEvent`, eventPayload);
      } else {
        await axiosInstance.put(`event/${eventId}/createEvent`, eventPayload);
      }
      toast({
        title: "Success",
        description: "Event created successfully",
        variant: "success",
      });
      navigate("/admin/events");
    } catch (error) {
      console.error("Error creating event:", error);
      toast({
        title: "Error",
        description: "Failed to create event",
        variant: "destructive",
      });
    }
  };

  return {
    form,
    loading,
    handleSave,
    handlePartialSave,
    handleSendInvoice,
    handleSendBid,
    handleCreateEvent,
  };
};
